import type { Operation } from '../domain/types';
import { ApiError, api } from './api';
import type { NestDatabase } from './local-db';
import { applySnapshot } from './snapshot';
import { reportHealth } from './monitoring';
import { applyChanges } from './changes';

interface SyncLease { owner: string; expires: number }
const LEASE_DURATION = 30_000;

export class SyncEngine {
  private timer?: ReturnType<typeof setTimeout>;
  private task?: Promise<void>;
  private controller?: AbortController;
  private stopped = false;
  private rerun = false;
  private failures = 0;
  private readonly runnerId = crypto.randomUUID();

  constructor(private db: NestDatabase, private householdId: string, private onState: (state: string) => void = () => undefined) {}

  start() {
    this.stopped = false;
    window.addEventListener('online', this.trigger);
    document.addEventListener('visibilitychange', this.visibility);
    this.trigger();
  }

  async stop() {
    this.stopped = true;
    this.rerun = false;
    window.removeEventListener('online', this.trigger);
    document.removeEventListener('visibilitychange', this.visibility);
    clearTimeout(this.timer);
    this.controller?.abort();
    await this.task;
  }

  trigger = () => {
    if (this.task) this.rerun = true;
    else void this.run();
  };

  private visibility = () => {
    if (document.visibilityState === 'visible') this.trigger();
    else clearTimeout(this.timer);
  };

  run(): Promise<void> {
    if (this.task) return this.task;
    if (this.stopped || document.visibilityState !== 'visible') return Promise.resolve();
    clearTimeout(this.timer);
    this.rerun = false;
    const controller = new AbortController();
    this.controller = controller;
    this.task = this.reconcile(controller).finally(() => {
      this.task = undefined;
      this.controller = undefined;
      if (this.stopped || document.visibilityState !== 'visible') return;
      const delay = this.rerun ? 0 : Math.min(60_000, 7_000 * 2 ** Math.min(this.failures, 3));
      this.timer = setTimeout(this.trigger, delay);
    });
    return this.task;
  }

  private async reconcile(controller: AbortController) {
    let leased = false;
    let heartbeat: ReturnType<typeof setInterval> | undefined;
    const signal = controller.signal;
    try {
      leased = await this.acquireLease();
      if (!leased) return;
      signal.throwIfAborted();
      // Requests are bounded, but a large outbox can take longer than one lease.
      heartbeat = setInterval(() => {
        void this.acquireLease().then((owned) => {
          if (!owned) controller.abort();
        }).catch(() => controller.abort());
      }, LEASE_DURATION / 3);
      this.onState('Syncing…');
      await this.flushOutbox(signal);
      signal.throwIfAborted();
      const localRevision = String((await this.db.meta.get(`revision:${this.householdId}`))?.value ?? '0');
      const remote = await api.revision(this.householdId, signal);
      if (BigInt(remote.revision) > BigInt(localRevision)) {
        try {
          const changes = await api.changes(this.householdId, localRevision, signal);
          signal.throwIfAborted();
          if (!await applyChanges(this.db, changes)) this.rerun = true;
        } catch (error) {
          // Permit old servers during a deployment and keep the recovery snapshot path.
          if (!(error instanceof ApiError && error.status === 404)) throw error;
          await applySnapshot(this.db, await api.snapshot(this.householdId, signal));
        }
      }
      this.failures = 0;
      const pending = await this.db.outbox.where('householdId').equals(this.householdId)
        .filter((op) => ['pending', 'sending', 'acknowledged'].includes(op.state)).count();
      if (!this.stopped) this.onState(pending ? `${pending} changes waiting to sync` : 'Up to date');
    } catch (error) {
      if (this.stopped) return;
      this.failures += 1;
      if(this.failures>=3&&navigator.onLine)reportHealth({event:'sync_failure',area:'sync',code:error instanceof ApiError?'server':'network'});
      if (error instanceof ApiError && error.status === 403) {
        await this.clearRevokedHousehold().catch(() => undefined);
        this.onState('Household access removed');
      } else if (error instanceof ApiError && error.status === 401) this.onState('Sign in to sync');
      else this.onState(navigator.onLine ? 'Sync needs attention' : 'Offline');
    } finally {
      clearInterval(heartbeat);
      if (leased) await this.releaseLease().catch(() => undefined);
    }
  }

  private async flushOutbox(signal: AbortSignal) {
    const operations = await this.db.outbox.where('householdId').equals(this.householdId)
      .filter((op) => op.state === 'pending' || op.state === 'sending').sortBy('createdAt');
    for (const queued of operations) {
      signal.throwIfAborted();
      // Re-read: an earlier acknowledgement may have advanced a dependent version.
      const operation = await this.db.outbox.get(queued.opId);
      if (!operation || !['pending', 'sending'].includes(operation.state)) continue;
      if (operation.predecessorOpId) {
        const predecessor = await this.db.outbox.get(operation.predecessorOpId);
        if (predecessor && predecessor.state !== 'acknowledged') continue;
      }
      await this.db.outbox.update(operation.opId, { state: 'sending', attempts: operation.attempts + 1 });
      try {
        const result = await api.mutate(operation, signal);
        signal.throwIfAborted();
        if (result.status === 'conflict') {
          await this.recordConflict(operation, 'A newer saved version exists.', result.canonical);
          continue;
        }
        await this.db.transaction('rw', [this.db.outbox, this.db.meta], async () => {
          const revision = String((await this.db.meta.get(`revision:${this.householdId}`))?.value ?? '0');
          if (BigInt(revision) >= BigInt(result.householdRevision)) await this.db.outbox.delete(operation.opId);
          else await this.db.outbox.update(operation.opId, { state: 'acknowledged', acknowledgedRevision: result.householdRevision, acknowledgedEntityVersion: result.entityVersion, error: undefined });
          if (result.entityVersion !== undefined) {
            await this.db.outbox.where('predecessorOpId').equals(operation.opId)
              .filter((op) => op.state === 'pending').modify({ expectedVersion: result.entityVersion });
          }
          const key = `minimumRevision:${this.householdId}`;
          const existing = String((await this.db.meta.get(key))?.value ?? '0');
          if (BigInt(result.householdRevision) > BigInt(existing)) await this.db.meta.put({ key, value: result.householdRevision });
        });
      } catch (error) {
        if (error instanceof ApiError && [400, 403, 404, 409, 422].includes(error.status)) {
          if (error.status === 409) {
            const body = error.details as { details?: { canonical?: unknown } } | undefined;
            await this.recordConflict(operation, error.message, body?.details?.canonical ?? null);
          } else await this.db.outbox.update(operation.opId, { state: 'rejected', error: error.message });
          continue;
        }
        await this.db.outbox.update(operation.opId, { state: 'pending', error: error instanceof Error ? error.message : 'Network error' });
        throw error;
      }
    }
  }

  private async recordConflict(operation: Operation, error: string, canonical: unknown) {
    await this.db.transaction('rw', [this.db.outbox, this.db.conflicts], async () => {
      await this.db.outbox.update(operation.opId, { state: 'conflict', error });
      await this.db.conflicts.put({ id: operation.opId, householdId: operation.householdId, entityId: operation.entityId, operation, canonical, createdAt: new Date().toISOString() });
    });
  }

  private async acquireLease(): Promise<boolean> {
    const key = `syncLease:${this.householdId}`;
    return this.db.transaction('rw', this.db.meta, async () => {
      const lease = (await this.db.meta.get(key))?.value as SyncLease | undefined;
      if (lease && lease.owner !== this.runnerId && lease.expires > Date.now()) return false;
      await this.db.meta.put({ key, value: { owner: this.runnerId, expires: Date.now() + LEASE_DURATION } });
      return true;
    });
  }

  private async releaseLease() {
    const key = `syncLease:${this.householdId}`;
    await this.db.transaction('rw', this.db.meta, async () => {
      if (((await this.db.meta.get(key))?.value as SyncLease | undefined)?.owner === this.runnerId) await this.db.meta.delete(key);
    });
  }

  private async clearRevokedHousehold(){const id=this.householdId;await this.db.transaction('rw',[this.db.households,this.db.memberships,this.db.categories,this.db.paymentMethods,this.db.transactions,this.db.budgets,this.db.recurringTemplates,this.db.occurrences,this.db.notifications,this.db.drafts,this.db.conflicts,this.db.outbox],async()=>{await Promise.all([this.db.households.delete(id),this.db.memberships.where('householdId').equals(id).delete(),this.db.categories.where('householdId').equals(id).delete(),this.db.paymentMethods.where('householdId').equals(id).delete(),this.db.transactions.where('householdId').equals(id).delete(),this.db.budgets.where('householdId').equals(id).delete(),this.db.recurringTemplates.where('householdId').equals(id).delete(),this.db.occurrences.where('householdId').equals(id).delete(),this.db.notifications.where('householdId').equals(id).delete(),this.db.drafts.where('householdId').equals(id).delete(),this.db.conflicts.where('householdId').equals(id).delete()]);const pending=await this.db.outbox.where('householdId').equals(id).toArray();await this.db.outbox.bulkPut(pending.map((operation)=>({...operation,payload:{redacted:true},state:'rejected' as const,error:'Household access was removed.'})));});}
}
